import {
  BackendError,
  CalculatedCostState,
  CalculatedCostWithTradeType,
  FuturesCalculatedCost,
  FuturesInitialMargin,
  isDefined,
  Loadable,
  LoadableState,
  OrderResponseMessage,
  OrderType,
  TempOrder,
  TradeType,
  WithRequired,
} from '@eih-netbroker/common';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useEStream } from '../../../shared/services/sse/useEStream';
import { useLogger } from '../../../shared/utils/useLogger';
import { uuid } from '../../../shared/utils/uuidUtils';
import { useFuturesInfo } from '../services/order/useFuturesInfo';
import { OrderFlowStep } from '../types/orderTypes';
import { OrderFormData } from './order/OrderForm';
import { OrderCurrencyExchangeSourceAndTargetCurrencyManagerProvider } from './OrderCurrencyExchangeSourceAndTargetCurrencyManagerProvider';
import { OrderCurrencyExchangeState, OrderCurrencyExchangeStateParams } from './OrderCurrencyExchangeStateContext';

type RequiredOrderProps = 'quantity' | 'instrument' | 'orderType';
type TransactionInfoGetterParams = WithRequired<OrderFormData, RequiredOrderProps> & {
  price?: number;
};

export type OrderStateContextProps =
  | undefined
  | {
      isCostCalculated: boolean;
      calculatedCostState?: CalculatedCostState;
      tempOrder?: TempOrder;
      setTempOrder: (tempOrder?: TempOrder) => void;
      warnings?: OrderResponseMessage[];
      smsCode?: string;
      setSmsCode: (smsCode?: string) => void;
      smsCodeError?: BackendError | undefined;
      setSmsCodeError: (smsCodeError?: BackendError) => void;
      setWarnings: (warnings?: OrderResponseMessage[]) => void;
      continueOrder: (
        confirmOrder: (twoFactor: boolean) => Promise<void>,
        generateSmsCode: () => Promise<void>,
      ) => Promise<void>;
      updateCalculatedCostState: (calculatedCostState?: CalculatedCostState) => void;
      getOrderPrice: (order: WithRequired<OrderFormData, RequiredOrderProps> & { price?: number }) => Promise<number>;
      getCalculatedCost: () => LoadableState<CalculatedCostWithTradeType>;
      getOrderTotalPrice: (params: TransactionInfoGetterParams) => Promise<number>;
      getFuturesOrderTransactionInfo: (
        params: TransactionInfoGetterParams,
      ) => Promise<FuturesCalculatedCost | undefined>;
      transactionId?: string;
      generateNewTransactionId: () => void;
      availableBalance: LoadableState<number>;
      setAvailableBalance: (availableBalance: LoadableState<number>) => void;
      orderFlowStep: OrderFlowStep;
      setOrderFlowStep: (step: OrderFlowStep) => void;
    };

export const OrderStateContext = createContext<OrderStateContextProps>(undefined);

export const OrderStateProvider = ({
  children,
  tempOrder,
  warnings,
  initialTransactionId,
  orderCurrencyExchangeStateParams = {},
}: {
  children: React.ReactNode;
  tempOrder?: TempOrder;
  warnings?: OrderResponseMessage[];
  initialTransactionId?: string;
  orderCurrencyExchangeStateParams?: OrderCurrencyExchangeStateParams;
}) => {
  const [calculatedCostState, setCalculatedCostState] = useState<CalculatedCostState>();
  const [displayedTempOrder, setDisplayedTempOrder] = useState<TempOrder | undefined>(tempOrder);
  const [displayedWarnings, setDisplayedWarnings] = useState<OrderResponseMessage[] | undefined>(warnings);
  const [smsCode, setSmsCode] = useState<string | undefined>();
  const [smsCodeError, setSmsCodeError] = useState<BackendError | undefined>();
  const [availableBalance, setAvailableBalance] = useState<LoadableState<number>>(Loadable.uninitialized);
  const [orderFlowStep, setOrderFlowStep] = useState<OrderFlowStep>(
    isDefined(initialTransactionId) ? OrderFlowStep.Confirmation : OrderFlowStep.Form,
  );
  const transactionIdRef = useRef(initialTransactionId ?? uuid());
  const { loadInstrumentSingle } = useEStream();
  const { calculateFuturesCost } = useFuturesInfo();
  const logger = useLogger('OrderStateProvider');

  const generateNewTransactionId = useCallback(() => {
    transactionIdRef.current = uuid();
  }, []);

  const isCostCalculated = useMemo(() => {
    return isDefined(calculatedCostState) && !calculatedCostState.isLoading;
  }, [calculatedCostState]);

  /**
   * Returns the price of the order:
   *  - for limit order the price is defined by the user
   *  - for market order the price is the last price of the instrument
   *
   * This function will throw an error if the order type is unknown or no price is available for the instrument
   */
  const getOrderPrice = useCallback(
    async (order: WithRequired<OrderFormData, RequiredOrderProps> & { price?: number }) => {
      if (!order.instrument) {
        throw new Error('Instrument is required');
      }
      if (
        order.orderType === OrderType.Limit ||
        order.orderType === OrderType.StopLimit ||
        order.orderType === OrderType.Oco
      ) {
        if (isDefined(order.price)) {
          return order.price;
        }
        throw new Error('Price is not defined for the limit order');
      } else if (order.orderType === OrderType.Market || order.orderType === OrderType.StopMarket) {
        const lastPrice = (await loadInstrumentSingle(order.instrument)).price?.value;
        if (isDefined(lastPrice)) {
          return lastPrice;
        }
        throw new Error('Last price is not available at the moment');
      }
      throw new Error('Unknown order type');
    },
    [loadInstrumentSingle],
  );

  /**
   * Returns the total price of the order (NET)
   */
  const getOrderTotalPrice = useCallback(
    async (
      order: WithRequired<OrderFormData, RequiredOrderProps> & {
        price?: number;
      },
    ) => {
      const price = await getOrderPrice(order);
      return price * order.quantity;
    },
    [getOrderPrice],
  );

  /**
   * Returns the margin requirement and net value for futures order
   */
  const getFuturesOrderTransactionInfo = useCallback(
    async (
      order: WithRequired<OrderFormData, 'quantity' | 'instrument' | 'orderType'> & {
        price?: number;
        futuresInitialMargin?: FuturesInitialMargin;
      },
    ) => {
      const price = await getOrderPrice(order);
      return calculateFuturesCost({ price, quantity: order.quantity });
    },
    [getOrderPrice, calculateFuturesCost],
  );

  const getCalculatedCost = useCallback((): LoadableState<CalculatedCostWithTradeType> => {
    if (calculatedCostState && calculatedCostState.isLoaded) {
      if (calculatedCostState.calculatedCost.tradeType === TradeType.Buy) {
        return Loadable.loaded({
          ...calculatedCostState.calculatedCost,
          amount: calculatedCostState.calculatedCost.buyAmount,
        });
      } else {
        return Loadable.loaded({
          ...calculatedCostState.calculatedCost,
          amount: calculatedCostState.calculatedCost.sellAmount,
        });
      }
    } else if (calculatedCostState?.hasError) {
      return Loadable.failed;
    }
    return Loadable.loading;
  }, [calculatedCostState]);

  const continueOrder = useCallback(
    async (confirmOrder: (twoFactor: boolean) => Promise<void>, generateSmsCode: () => Promise<void>) => {
      try {
        if (!displayedTempOrder) {
          return;
        }
        const newTempOrder = { ...displayedTempOrder };
        if (displayedWarnings) {
          await confirmOrder(false);
          setDisplayedWarnings(undefined);
        } else if (displayedTempOrder?.showWarningConfirmation) {
          if (!displayedTempOrder.showConfirmation && !displayedTempOrder.showSmsConfirmation) {
            await confirmOrder(false);
          }
          newTempOrder.showWarningConfirmation = false;
          setDisplayedTempOrder(newTempOrder);
        } else if (displayedTempOrder?.showConfirmation) {
          if (!displayedTempOrder.showSmsConfirmation) {
            await confirmOrder(false);
          } else {
            try {
              await generateSmsCode();
              setOrderFlowStep(OrderFlowStep.TwoFactor);
            } catch (error) {
              logger.error('Failed to generate SMS code', error);
            }
          }

          newTempOrder.showConfirmation = false;
          setDisplayedTempOrder(newTempOrder);
        } else if (displayedTempOrder?.showSmsConfirmation) {
          await confirmOrder(true);
          newTempOrder.showSmsConfirmation = false;
          setDisplayedTempOrder(newTempOrder);
        }
      } catch (err) {
        // catched in the confirmOrder function
      }
    },
    [displayedTempOrder, displayedWarnings, logger],
  );

  const value = useMemo(
    () => ({
      isCostCalculated,
      calculatedCostState,
      tempOrder: displayedTempOrder,
      warnings: displayedWarnings,
      smsCode,
      smsCodeError,
      setTempOrder: setDisplayedTempOrder,
      continueOrder,
      updateCalculatedCostState: setCalculatedCostState,
      getOrderPrice,
      getOrderTotalPrice,
      getCalculatedCost,
      setWarnings: setDisplayedWarnings,
      setSmsCode,
      setSmsCodeError,
      transactionId: transactionIdRef.current,
      generateNewTransactionId,
      availableBalance,
      setAvailableBalance,
      getFuturesOrderTransactionInfo,
      orderFlowStep,
      setOrderFlowStep,
    }),
    [
      isCostCalculated,
      calculatedCostState,
      displayedTempOrder,
      displayedWarnings,
      smsCode,
      smsCodeError,
      setDisplayedTempOrder,
      continueOrder,
      setCalculatedCostState,
      getOrderPrice,
      getOrderTotalPrice,
      getCalculatedCost,
      setDisplayedWarnings,
      setSmsCodeError,
      transactionIdRef,
      generateNewTransactionId,
      availableBalance,
      setAvailableBalance,
      getFuturesOrderTransactionInfo,
      orderFlowStep,
      setOrderFlowStep,
    ],
  );

  return (
    <OrderStateContext.Provider value={value}>
      <OrderCurrencyExchangeSourceAndTargetCurrencyManagerProvider>
        <OrderCurrencyExchangeState
          exchangeRate={orderCurrencyExchangeStateParams.exchangeRate}
          exchangeSettlementDate={orderCurrencyExchangeStateParams.exchangeSettlementDate}
        >
          {children}
        </OrderCurrencyExchangeState>
      </OrderCurrencyExchangeSourceAndTargetCurrencyManagerProvider>
    </OrderStateContext.Provider>
  );
};

export const useOrderState = () => {
  const context = useContext(OrderStateContext);
  if (context === undefined) {
    throw new Error('useOrderState must be used within a OrderProvider');
  }
  return context;
};
